import {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {QuestType} from "../types";


type FetchQuestTypeMethod = (quiet?: boolean, refresh?: boolean) => Promise<QuestType>;
interface UseQuestTypeReturn {
  questType: QuestType | null;
  fetched: boolean,
  refresh: FetchQuestTypeMethod;
}

export const useQuestType = (id: string): UseQuestTypeReturn => {
  const {
    getQuestType
  } = useApi();

  const [questType, setQuestType] = useState<QuestType | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchQuestType: FetchQuestTypeMethod = async(quiet = false, refresh = false) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getQuestType", [id]);

    try {
      if(!id) throw new Error("No id provided");
      const questTypes = await withCache(getQuestType, [id], 300_000);
      setQuestType(questTypes);
      return questTypes;
    } catch (e) {
      console.error(`Error fetching quest type ${id}`, e);
      throw new Error(`Error fetching quest type ${id}`, {cause: e});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    fetchQuestType();
  }, [id]);

  return {
    fetched,
    questType,
    refresh: quiet => fetchQuestType(quiet, true)
  };
};

type FetchQuestTypesMethod = (quiet?: boolean, refresh?: boolean) => Promise<QuestType[]>;
interface UseQuestTypesReturn {
  questTypes: QuestType[];
  fetched: boolean,
  refresh: FetchQuestTypesMethod;

  // @deprecated use questTypes instead
  questTypesList: QuestType[];
}

export const useQuestTypes: (params: {[key: string]: any}) => UseQuestTypesReturn = (params = {}) => {
  const {getQuestTypes} = useApi();

  const [questTypes, setQuestTypes] = useState<QuestType[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchQuestTypes: FetchQuestTypesMethod = async(quiet = false) => {
    if(!quiet) setFetched(false);

    try {
      const questTypes = await withCache(getQuestTypes, [params], 300_000);
      setQuestTypes(questTypes);
      return questTypes;
    } catch (e) {
      console.error("Error fetching quest types", e);
      throw new Error("Error fetching quest types", {cause: e});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    fetchQuestTypes();
  }, [JSON.stringify(params)]);

  return {
    fetched,
    questTypes,
    questTypesList: questTypes,
    refresh: quiet => fetchQuestTypes(quiet, true)
  };
};


type FetchQuestTypeCategoriesMethod = (quiet?: boolean, refresh?: boolean) => Promise<string[]>;
interface UseQuestTypeCategoriesReturn {
  categories: string[];
  fetched: boolean,
  refresh: FetchQuestTypeCategoriesMethod;
}

export const useQuestTypeCategories = (params: {[key: string]: any} = {}): UseQuestTypeCategoriesReturn => {
  const {getQuestTypeCategories} = useApi();

  const [categories, setCategories] = useState<string[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchQuestTypeCategories: FetchQuestTypeCategoriesMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getQuestTypeCategories", [params]);

    try {
      const categories = await withCache(getQuestTypeCategories, [params], 300_000);
      setCategories(categories);
      return categories;
    } catch (e) {
      console.error("Error fetching quest type categories", e);
      throw new Error("Error fetching quest type categories", {cause: e});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    fetchQuestTypeCategories();
  }, [JSON.stringify(params)]);

  return {
    categories,
    fetched,
    refresh: quiet => fetchQuestTypeCategories(quiet, true)
  };
};
